import React, { FC, useCallback } from 'react';
import styles from './index.module.scss';
import {
  AssignToQuestion,
  TaskFulfillmentPicker,
  TaskRequirementPicker,
  TaskResponsePicker,
  TaskStatusPicker
} from 'src/shared';
import { Button, confirm } from 'src/common';
import { useTranslation } from 'react-i18next';
import modalStyles from 'src/common/Modal/index.module.scss';
import { useBidWorkspace } from 'src/models/bids/Bids/hooks';
import { useDeleteBidTasks, useTask } from 'src/models/bids/BidTask/hooks';
import { BidQuestionType } from 'src/models/bids/BidFull/types';
import { useNavigate } from 'react-router';
import { useBidUrl } from 'src/models/procurements/hooks';
import { useTasks } from 'src/models/bids/BidFull/hooks';

export const BidTaskToolbar: FC = () => {
  const { data: task } = useTask();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const bidId = task?.bidId;
  const groupId = task?.groupId;
  const { workspace } = useBidWorkspace(bidId);
  const [deleteTasks] = useDeleteBidTasks();
  const bidUrl = useBidUrl(bidId ?? '');
  const allTasks = useTasks();

  const getNextTaskToNavigate = useCallback(
    (taskId: string) => {
      const generatedTasks = allTasks.generated.data?.data;
      const manualTasks = allTasks.manual.data?.data;
      let currentTaskIdx: number | undefined = undefined;
      let nextTask: { id: string | undefined; type: BidQuestionType } | undefined;

      if (generatedTasks) {
        currentTaskIdx = generatedTasks.tasks.findIndex(generatedTask => generatedTask && generatedTask.id === taskId);

        if (currentTaskIdx === 0) {
          nextTask = {
            id: generatedTasks.tasks[1]?.id,
            type: BidQuestionType.Generated
          };
        } else if (currentTaskIdx > 0) {
          nextTask = {
            id: generatedTasks.tasks[currentTaskIdx - 1]?.id,
            type: BidQuestionType.Generated
          };
        } else {
          currentTaskIdx = undefined;
        }
      }

      const isOnlyGeneratedTask =
        !!generatedTasks && currentTaskIdx !== undefined && currentTaskIdx >= 0 && generatedTasks.totalCount === 1;

      if ((currentTaskIdx === undefined || isOnlyGeneratedTask) && manualTasks) {
        if (isOnlyGeneratedTask) {
          nextTask = {
            id: manualTasks.tasks[0]?.id,
            type: BidQuestionType.Manual
          };
        } else {
          currentTaskIdx = manualTasks.tasks.findIndex(manualTask => manualTask && manualTask.id === taskId);

          if (currentTaskIdx === 0) {
            nextTask = {
              id: manualTasks.tasks[1]?.id,
              type: BidQuestionType.Manual
            };
          } else if (currentTaskIdx > 0) {
            nextTask = {
              id: manualTasks.tasks[currentTaskIdx - 1]?.id,
              type: BidQuestionType.Manual
            };
          }
        }
      }

      return nextTask;
    },
    [allTasks]
  );

  const correctOffset = useCallback(
    (type: BidQuestionType) => {
      if (type === BidQuestionType.Generated) {
        const currentOffset = allTasks.generated.offset;
        allTasks.generated.setOffset(currentOffset - 1);
      } else {
        const currentOffset = allTasks.manual.offset;
        allTasks.manual.setOffset(currentOffset - 1);
      }
    },
    [allTasks]
  );

  const navigateTask = useCallback(
    (currentTaskId: string) => {
      const nextTask = getNextTaskToNavigate(currentTaskId);

      if (nextTask && nextTask.id) {
        correctOffset(nextTask.type);
        // TODO: @checkme
        navigate(`/task/${nextTask.id}`);
      } else {
        bidId && navigate(bidUrl, { replace: true });
      }
    },
    [getNextTaskToNavigate, correctOffset, navigate, bidId, bidUrl]
  );

  const onRemoveTask = useCallback(() => {
    if (!task) return;
    !task.isGenerated &&
      confirm.confirm({
        title: t('BiddingTool.deleteTask'),
        okText: t('Common.delete'),
        content: t('BiddingTool.deleteTaskWarning'),
        cancelText: t('Common.cancel'),
        className: modalStyles.confirmModal,
        centered: true,
        onOk() {
          groupId &&
            deleteTasks({
              questionIds: [task.id],
              groupId,
              type: BidQuestionType.Manual,
              mode: 'single'
            });
          navigateTask(task.id);
        }
      });
  }, [task, t, groupId, deleteTasks, navigateTask]);

  return (
    <>
      {task && groupId && (
        <div className={styles.topBar}>
          <div className={styles.actions}>
            <TaskFulfillmentPicker
              groupId={groupId}
              questions={[task]}
              taskValue={task.fulfilment}
              type={task.type}
              mode="single"
            />
            <TaskRequirementPicker groupId={groupId} questions={[task]} taskValue={task.requirementType} />
            <TaskResponsePicker
              groupId={groupId}
              questions={[task]}
              taskValue={task.responseType}
              type={task.type}
              mode="single"
            />
            <TaskStatusPicker
              groupId={groupId}
              questions={[task]}
              taskValue={task.status}
              type={task.type}
              mode="single"
            />
            {bidId && (
              <AssignToQuestion
                groupId={groupId}
                type={task.type}
                assignedTo={task.assignedTo}
                questions={[task]}
                wsId={workspace?.id}
                bidId={bidId}
                assignToMode="single"
              />
            )}
          </div>
          {!task.isGenerated && (
            <Button type={'default'} onClick={onRemoveTask}>
              {t('Common.delete')}
            </Button>
          )}
        </div>
      )}
    </>
  );
};

export default BidTaskToolbar;
